import { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Input from "ds/components/Input";
import { VcsProvider } from "types/generated";

import VCSIntegrationsDocumentationButton from "../../components/DocumentationButton";
import GitCheckoutToggle from "../../components/GitCheckoutToggle";
import VCSChecksToggle from "../../components/VCSChecksToggle";
import { BUILT_IN_GITHUB_INTEGRATION_NAME } from "../../constants";
import useCloseFullScreenModal from "../../useCloseFullScreenModal";
import { UpdateGitHubIntegrationData } from "./gql";
import { getFormDefaultValues, mapUpdateGitHubIntegrationInputValues } from "./helpers";
import { ManageGitHubManualFormFields } from "./types";
import useUpdateGitHubIntegration from "./useUpdateGitHubIntegration";

type VCSIntegrationsManageGitHubManualProps = {
  integration?: UpdateGitHubIntegrationData;
  onIsDirtyChange: (value: boolean) => void;
};

const VCSIntegrationsManageGitHubManual = ({
  integration,
  onIsDirtyChange,
}: VCSIntegrationsManageGitHubManualProps) => {
  const { handleOnClose } = useCloseFullScreenModal();

  const manageGitHubManualForm = useForm<ManageGitHubManualFormFields>({
    defaultValues: getFormDefaultValues(integration),
    mode: "onChange",
  });

  const {
    formState: { isDirty },
    handleSubmit,
  } = manageGitHubManualForm;

  const { updateGitHubIntegration, loading: updateLoading } = useUpdateGitHubIntegration();

  const handleSetup = useCallback(
    () =>
      handleSubmit((formData: ManageGitHubManualFormFields) => {
        updateGitHubIntegration(mapUpdateGitHubIntegrationInputValues(formData), handleOnClose);
      })(),
    [handleOnClose, updateGitHubIntegration, handleSubmit]
  );

  useEffect(() => {
    onIsDirtyChange(isDirty);
  }, [isDirty, onIsDirtyChange]);

  return (
    <FormProvider {...manageGitHubManualForm}>
      <FullScreenModalBody>
        <FormField
          label="Integration name"
          helperText="The integration name is unique and cannot be changed"
          noMargin
        >
          {({ ariaInputProps }) => (
            <Input value={BUILT_IN_GITHUB_INTEGRATION_NAME} disabled {...ariaInputProps} />
          )}
        </FormField>

        <GitCheckoutToggle />
        <VCSChecksToggle />
      </FullScreenModalBody>
      <FullScreenModalFooter justify="between">
        <VCSIntegrationsDocumentationButton provider={VcsProvider.Github} />

        <Button onPress={handleSetup} variant="primary" loading={updateLoading}>
          Save
        </Button>
      </FullScreenModalFooter>
    </FormProvider>
  );
};

export default VCSIntegrationsManageGitHubManual;
